import type {CmLogger} from "@cm/log"
import {TemplateNode} from "#template/template-node/template-node"
import {TemplateListNode} from "#template/template-list-node/template-list-node"
import type {NodeParameters} from "@cm/graph-v3"
import {GraphNode, TraversalAction, mapGraphParameters, traverseGraphParameters} from "@cm/graph-v3"
import {isClass, isInstanceOf} from "@cm/utils"
import * as changeCase from "change-case"
import {
    isNodeOwnerLikeNode,
    isTemplateContainer,
    isTemplateOwningContainer,
    isTemplateReferenceContainer,
    isValueLikeNode,
    type NodeOwnerLikeNode,
    type TemplateContainer,
    type TemplateOwningContainer,
} from "#template/role-markers"
import {isMaterialLikeNode} from "#template/nodes/material/fundamental"
import {OutputNode} from "#template/fundamental/output/output-node"
import type {SwitchNode} from "#template/fundamental/switch/switch-node"
import {LodType} from "#template/nodes/boolean/lod-type"
import {getLodTypeDescription} from "#template/value-types"
import {RigidRelation} from "#template/nodes/rigid-relation/rigid-relation"
import {MaterialAssignment} from "#template/nodes/material-assignment/material-assignment"
import {MaterialAssignments} from "#template/nodes/material-assignment/material-assignments"
import {Parameters} from "#template/nodes/parameters"
import type {TemplateGraph} from "#template/nodes/template-graph/template-graph"

const isGraphNode = (value: unknown): boolean => value instanceof GraphNode

export const isTemplateNode = (x: unknown): x is TemplateNode => x instanceof TemplateNode

export const isNamedNode = (x: unknown): x is TemplateNode<{name: string}> =>
    x instanceof TemplateNode && typeof (x.parameters as {name?: unknown}).name === "string"

export const getTemplateNodeClassLabel = (node: TemplateNode | string) => {
    return changeCase.capitalCase(typeof node === "string" ? node : node.nodeClass)
}

export const getTemplateNodeLabel = (node: TemplateNode | null): string => {
    if (!node) return "???"

    if (isNamedNode(node) && node.parameters.name.length > 0) return node.parameters.name

    if (node instanceof RigidRelation) return `Align [${getTemplateNodeLabel(node.parameters.targetA)}] to [${getTemplateNodeLabel(node.parameters.targetB)}]`
    else if (isValueLikeNode(node)) return `Value: ${JSON.stringify(node.parameters.value)}`
    else if (node instanceof LodType) return `LOD Type: ${getLodTypeDescription(node.parameters.lodType)}`

    return getTemplateNodeClassLabel(node)
}

// Strips the trailing " Switch" from a `*Switch` class label (e.g. "Number Switch" → "Number"). Stays
// callable on the abstract SwitchNode base.
export const getTemplateSwitchItemLabel = (node: SwitchNode) => {
    return getTemplateNodeClassLabel(node).replace(" Switch", "")
}

export function getUniqueTemplateNodeParent(node: TemplateNode, callback?: (parent: TemplateNode) => void): TemplateNode {
    if (node.parents.size === 1) {
        const [parent] = node.parents

        if (parent instanceof TemplateNode) {
            if (callback) callback(parent)
            return parent
        }
    }

    throw new Error("Node has invalid number of node parents")
}

// The direct child nodes of a node owner (TemplateGraph / Group / ConfigVariant / ConfigGroup /
// mattress component graphs / ...), i.e. the entries of its `nodes` container. Convenience over
// `owner.parameters.nodes.parameters.list`. Generic over the container's item type so specialized
// owners (e.g. a mattress component graph) keep their narrower node type.
export function getOwnedNodes<T extends TemplateNode>(owner: TemplateNode<{nodes: TemplateListNode<T>}>): readonly T[]
export function getOwnedNodes<T extends TemplateNode, S extends T>(
    owner: TemplateNode<{nodes: TemplateListNode<T>}>,
    ctor: abstract new (...args: never[]) => S,
): readonly S[]
export function getOwnedNodes<T extends TemplateNode>(
    owner: TemplateNode<{nodes: TemplateListNode<T>}>,
    ctor?: abstract new (...args: never[]) => T,
): readonly T[] {
    const nodes = owner.parameters.nodes.entries
    return ctor ? nodes.filter(isInstanceOf(ctor)) : nodes
}

// `getNodeOwner` walks up from a node (or directly from a TemplateNodes-style container) to the owning
// node (TemplateGraph / Group / ConfigVariant / ConfigGroup). Single-parent invariant: throws if the
// node has multiple TemplateNodes parents.
export function getNodeOwner(node: TemplateNode, callback?: (nodeOwner: NodeOwnerLikeNode) => void): NodeOwnerLikeNode | null {
    if (isTemplateOwningContainer(node)) {
        const nodeOwner = getUniqueTemplateNodeParent(node)
        if (!isNodeOwnerLikeNode(nodeOwner)) throw new Error("TemplateNodes has no node-owner parent")
        if (callback) callback(nodeOwner)
        return nodeOwner
    }

    const containerParents = [...node.parents].filter(isTemplateOwningContainer)
    if (containerParents.length === 0) return null
    else if (containerParents.length === 1) {
        const [container] = containerParents
        return getNodeOwner(container, callback)
    } else throw new Error("Node has multiple node parents")
}

export function isEvaluatedTemplateNode(node: TemplateNode, activeNodeSet?: Set<TemplateNode>) {
    return [...node.parents].some(
        (parent) => !(parent instanceof TemplateNode) || (!isTemplateOwningContainer(parent) && (!activeNodeSet || activeNodeSet.has(parent))),
    )
}

type DeletableNode = TemplateNode

export function expandTemplateNodesToDelete(nodes: Set<TemplateNode>): Set<DeletableNode> {
    const nodesToDelete = new Set<DeletableNode>()
    const collectNodesToDelete = (node: TemplateNode) => {
        nodesToDelete.add(node)
        if (isNodeOwnerLikeNode(node)) {
            nodesToDelete.add(node.parameters.nodes)
            node.parameters.nodes.parameters.list.forEach(collectNodesToDelete)
        }
    }
    nodes.forEach(collectNodesToDelete)

    return nodesToDelete
}

export type ReferencingDeletedTemplateNodes = Map<TemplateNode, Map<string, TemplateNode>>

export function getReferencingDeletedTemplateNodes(templateGraph: TemplateGraph, nodes: Set<TemplateNode> | Set<DeletableNode>) {
    const nodesToDelete =
        nodes instanceof Set && [...nodes].some((n) => n instanceof TemplateListNode)
            ? (nodes as Set<DeletableNode>)
            : expandTemplateNodesToDelete(nodes as Set<TemplateNode>)

    const nodesToDeleteOwningParentContainers = new Set<TemplateOwningContainer>()
    nodesToDelete.forEach((node) => {
        node.parents.forEach((parent) => {
            if (isTemplateOwningContainer(parent) && !nodesToDelete.has(parent)) nodesToDeleteOwningParentContainers.add(parent)
        })
    })

    const addTemplateNodeReference = (referencingDeletedPaths: ReferencingDeletedTemplateNodes, node: TemplateNode, path: string, reference: TemplateNode) => {
        const existing = referencingDeletedPaths.get(node)
        if (existing) existing.set(path, reference)
        else {
            const newMap = new Map<string, TemplateNode>()
            newMap.set(path, reference)
            referencingDeletedPaths.set(node, newMap)
        }
    }

    const passReferenceDeleteToParentLibrary: {
        appliesTo: (node: TemplateNode) => boolean
        passChildReferenceDeleteToParent: (child: TemplateNode) => boolean
        mapToParentParameters: (node: TemplateNode) => ReferencingDeletedTemplateNodes
    }[] = [
        {
            appliesTo: (node) => node instanceof MaterialAssignment && getUniqueTemplateNodeParent(node) instanceof MaterialAssignments,
            passChildReferenceDeleteToParent: (child) => isMaterialLikeNode(child),
            mapToParentParameters: (node) => {
                const parent = getUniqueTemplateNodeParent(node)
                if (!(parent instanceof MaterialAssignments)) throw new Error("Invalid parent type for MaterialAssignment")

                const referencingDeletedPaths: ReferencingDeletedTemplateNodes = new Map()
                traverseGraphParameters(parent.parameters, isGraphNode, (child: unknown, path: string) => {
                    if (child === node) addTemplateNodeReference(referencingDeletedPaths, parent, path, node)
                })

                return referencingDeletedPaths
            },
        },
        {
            appliesTo: (node) => node instanceof OutputNode,
            passChildReferenceDeleteToParent: (child) => child.nodeClass === "TemplateInstance",
            mapToParentParameters: (node) => {
                const parent = getUniqueTemplateNodeParent(node)
                if (parent instanceof Parameters) {
                    const referencingDeletedPaths: ReferencingDeletedTemplateNodes = new Map()
                    traverseGraphParameters(parent.parameters, isGraphNode, (child: unknown, path: string) => {
                        if (child === node) addTemplateNodeReference(referencingDeletedPaths, parent, path, node)
                    })
                    return referencingDeletedPaths
                } else if (parent instanceof MaterialAssignment) {
                    const grandParent = getUniqueTemplateNodeParent(parent)
                    if (!(grandParent instanceof MaterialAssignments)) throw new Error("Invalid parent type for MaterialAssignment")

                    const referencingDeletedPaths: ReferencingDeletedTemplateNodes = new Map()
                    traverseGraphParameters(grandParent.parameters, isGraphNode, (child: unknown, path: string) => {
                        if (child === parent) addTemplateNodeReference(referencingDeletedPaths, grandParent, path, parent)
                    })
                    return referencingDeletedPaths
                }

                throw new Error("Invalid parent type for Output")
            },
        },
    ]

    const referencingDeletedPaths: ReferencingDeletedTemplateNodes = new Map()

    for (const node of getAllTemplateNodes(templateGraph)) {
        if (nodesToDelete.has(node)) continue
        if (isTemplateOwningContainer(node) && nodesToDeleteOwningParentContainers.has(node)) continue
        if (isTemplateReferenceContainer(node)) {
            const templateContainerParent = getUniqueTemplateNodeParent(node)
            if (nodesToDelete.has(templateContainerParent)) continue
        }

        const passChildReferenceDeleteToParentData = passReferenceDeleteToParentLibrary.filter((data) => data.appliesTo(node))
        if (passChildReferenceDeleteToParentData.length > 1) throw new Error("Not unique passChildDeleteToParentData")
        const passReferenceDeleteToParent = passChildReferenceDeleteToParentData.at(0)

        traverseGraphParameters(node.parameters, isGraphNode, (child: unknown, path: string) => {
            if (child instanceof TemplateNode) {
                if (nodesToDelete.has(child)) {
                    if (passReferenceDeleteToParent) {
                        const {passChildReferenceDeleteToParent, mapToParentParameters} = passReferenceDeleteToParent
                        if (passChildReferenceDeleteToParent(child)) {
                            const parentDeleteReferences = mapToParentParameters(node)
                            for (const [parent, children] of parentDeleteReferences) {
                                for (const [path, child] of children.entries()) addTemplateNodeReference(referencingDeletedPaths, parent, path, child)
                            }
                            return
                        }
                    }

                    addTemplateNodeReference(referencingDeletedPaths, node, path, child)
                }
            }
        })
    }

    return referencingDeletedPaths
}

const deleteToken = Symbol("deleteToken")

const removeDeleteTokens = (parameters: NodeParameters): NodeParameters => {
    const eraseParameters = (parameter: unknown): unknown => {
        if (typeof parameter === "object" && parameter) {
            if (parameter instanceof Array)
                return parameter
                    .filter((entry) => {
                        return entry !== deleteToken
                    })
                    .map((entry) => eraseParameters(entry))
            else if (isClass(parameter)) return parameter
            else
                return Object.entries(parameter).reduce<NodeParameters>((acc, [key, value]) => {
                    acc[key] = eraseParameters(value)
                    return acc
                }, {})
        } else return parameter
    }

    return eraseParameters(parameters) as NodeParameters
}

export function deleteNodesFromTemplateGraph(templateGraph: TemplateGraph, nodes: Set<TemplateNode>, logger: CmLogger) {
    const nodesToDelete = expandTemplateNodesToDelete(nodes)
    const referencingDeletedNodes = getReferencingDeletedTemplateNodes(templateGraph, nodesToDelete)

    const removeFromContainers: Map<TemplateContainer, Set<TemplateNode>> = new Map()
    const removeFromNodes: Map<TemplateNode, Map<string, {node: TemplateNode; newValue: null | undefined | typeof deleteToken}>> = new Map()
    for (const [node, children] of referencingDeletedNodes) {
        if (isTemplateContainer(node)) {
            removeFromContainers.set(node, new Set(children.values()))
        } else {
            const newValues: Map<string, {node: TemplateNode; newValue: null | undefined | typeof deleteToken}> = new Map()

            const schema = node.definition.validation.parameters
            for (const [childPath, child] of children) {
                const replacementValues = [deleteToken as typeof deleteToken, null, undefined]
                for (const replacementValue of replacementValues) {
                    const newParameters = removeDeleteTokens(
                        mapGraphParameters(node.parameters, isGraphNode, (innerNode: unknown, path: string) => {
                            if (child !== innerNode || path !== childPath) return innerNode
                            return replacementValue
                        }),
                    )

                    if (schema.safeParse(newParameters).success) {
                        newValues.set(childPath, {node: child, newValue: replacementValue})
                        break
                    }
                }

                if (!newValues.has(childPath)) {
                    logger.error("Failed to unreference node", node, children)
                    throw new Error(`Failed to unreference node ${getTemplateNodeLabel(node)}`)
                }
            }

            removeFromNodes.set(node, newValues)
        }
    }

    for (const [container, children] of removeFromContainers) {
        children.forEach((child) => {
            if (child instanceof TemplateNode) container.removeEntry(child)
        })
    }

    for (const [node, children] of removeFromNodes) {
        const newParameters = removeDeleteTokens(
            mapGraphParameters(node.parameters, isGraphNode, (innerNode: unknown, path: string) => {
                const child = children.get(path)
                if (!child) return innerNode
                if (child.node !== innerNode) return innerNode
                return child.newValue
            }),
        )

        node.replaceParameters(removeDeleteTokens(newParameters) as Parameters["parameters"])
    }

    nodesToDelete.forEach((node) => {
        node.parents.forEach((parent) => {
            if (isTemplateContainer(parent)) parent.removeEntry(node)
        })
    })

    const failedToDelete = new Set<TemplateNode>()
    for (const node of getAllTemplateNodes(templateGraph)) if (nodesToDelete.has(node)) failedToDelete.add(node)

    if (failedToDelete.size > 0) {
        logger.error("Failed to delete nodes", failedToDelete)
        throw new Error("Failed to successfully delete all nodes")
    }
}

export function getAllTemplateNodes(templateGraph: TemplateGraph) {
    const nodes = new Set<TemplateNode>()
    templateGraph.depthFirstTraversalPreorder(() => TraversalAction.Continue, nodes)

    return nodes
}
